import React, { useMemo, memo } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { useDispatch, useSelector } from "react-redux";
import {
  ENTITY_TYPES,
  prettyNumericRound,
  getFormattedNumber,
} from "../../utils";
import {
  getItemLevelSummations,
  getPogLevelSummations,
} from "../../utils/getSummations";
import NumericLabel from "../../components/NumericLabel";
import { calculateAvailableCreditLimit } from "../../store/globalSlice";

const SummaryTotalWrapperSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  backgroundColor: "#ffc1070f",
  borderColor: "grey",
  p: 1,
  mb: 1,
  border: "2px inset #e91e63db",
  borderRadius: "5px 5px 5px 5px",
};

const SummaryTitleSx = {
  fontWeight: 400,
  mb: 0.5,
  textShadow: "1px 0px 1px #ce59376b",
};

const PlanogramSummaryContentSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const OrderSummaryContentSx = {
  display: "flex",
  alignItems: "center",
};

const OrderSummarySpacingSx = { mr: 4 };

const SummationTitleSx = { display: "flex" };

const getCostValue = (value) => {
  const updatedValue = prettyNumericRound(value || 0);
  return `$${getFormattedNumber(updatedValue)}`;
};

const PlanogramSummary = memo(({ memoizedTotal, viewBy = "" }) => {
  return (
    <Grid container spacing={2} sx={PlanogramSummaryContentSx}>
      <>
        <Grid item xs="auto">
          <Typography variant="subtitle2" sx={SummationTitleSx}>
            Footage:&nbsp;
            <NumericLabel isSeparator value={memoizedTotal?.footage} />
            ft
          </Typography>
        </Grid>
        <Grid item xs="auto">
          <Typography variant="subtitle2" sx={SummationTitleSx}>
            POG SKUs:&nbsp;
            <NumericLabel isSeparator value={memoizedTotal?.pogSkus} />
          </Typography>
        </Grid>
        <Grid item xs="auto">
          <Typography variant="subtitle2" sx={SummationTitleSx}>
            POG Qty:&nbsp;
            <NumericLabel isSeparator value={memoizedTotal?.pogQty} />
          </Typography>
        </Grid>
      </>
      <Grid item xs="auto">
        <Typography variant="subtitle2" sx={SummationTitleSx}>
          POG Cost:&nbsp;
          {getCostValue(memoizedTotal?.pogCost || 0)}
        </Typography>
      </Grid>
      <Grid item xs="auto">
        <Typography variant="subtitle2" sx={SummationTitleSx}>
          Missing:&nbsp;
          <NumericLabel isSeparator value={memoizedTotal?.missingSkus} />
        </Typography>
      </Grid>
      <Grid item xs="auto">
        <Typography variant="subtitle2" sx={SummationTitleSx}>
          Adj Qty:&nbsp;
          <NumericLabel isSeparator value={memoizedTotal?.adjQty} />
        </Typography>
      </Grid>
      <Grid item xs="auto">
        <Typography variant="subtitle2" sx={SummationTitleSx}>
          Adj Cost:&nbsp;
          {getCostValue(memoizedTotal?.adjCost || 0)}
        </Typography>
      </Grid>
      <Grid item xs="auto">
        <Typography variant="subtitle2" sx={SummationTitleSx}>
          Total Cost:&nbsp;
          {getCostValue(memoizedTotal?.totalCost || 0)}
        </Typography>
      </Grid>
    </Grid>
  );
});

const OrderSummary = memo(({ memoizedTotal }) => {
  return (
    <Grid container spacing={2} sx={OrderSummaryContentSx}>
      <Grid item xs="auto" sx={OrderSummarySpacingSx}>
        <Typography variant="subtitle2" sx={SummationTitleSx}>
          Order Qty:&nbsp;
          <NumericLabel isSeparator value={memoizedTotal?.orderQty} />
        </Typography>
      </Grid>
      <Grid item xs="auto" sx={OrderSummarySpacingSx}>
        <Typography variant="subtitle2" sx={SummationTitleSx}>
          Order Cost:&nbsp;
          {getCostValue(memoizedTotal?.totalCost || 0)}
        </Typography>
      </Grid>
    </Grid>
  );
});

function SummaryCalculator(props) {
  const {
    itemDetails = [],
    pogDetails = {},
    selections = {},
    viewBy = "",
  } = props;
  const dispatch = useDispatch();
  const entityType = useSelector((store) => store.global.settings.entityType);
  const memoizedTotal = useMemo(() => {
    if (itemDetails.length) {
      return getItemLevelSummations(itemDetails);
    }
    const scopes = Object.keys(selections);
    let pogSelections = [];
    for (let k = 0; k < scopes.length; k++) {
      const currentScope = scopes[k];
      const selectedPogIds = Object.keys(selections[currentScope]) ?? [];
      pogSelections = [...pogSelections, ...selectedPogIds];
    }
    const grandTotals = getPogLevelSummations(pogDetails, pogSelections);
    dispatch(calculateAvailableCreditLimit(grandTotals.totalCost));
    return grandTotals;
  }, [itemDetails, pogDetails, dispatch, selections]);

  const summaryTitle = useMemo(
    () => (itemDetails.length ? "Summary Total" : "Grand Total"),
    [itemDetails.length]
  );

  return (
    <Paper variant="outlined" sx={SummaryTotalWrapperSx}>
      <Typography variant="subtitle1" sx={SummaryTitleSx}>
        {summaryTitle}
      </Typography>
      {entityType === ENTITY_TYPES.planogram ? (
        <PlanogramSummary memoizedTotal={memoizedTotal} viewBy={viewBy} />
      ) : (
        <OrderSummary memoizedTotal={memoizedTotal} />
      )}
    </Paper>
  );
}

export default memo(SummaryCalculator);
